import type {
  ActorRole,
  CardDeck,
  FinalStatus,
  GameCard,
  GameMode,
  GameSession,
  PenetrationLevel,
  SessionSummary
} from "../types";

type RandomFn = () => number;
type QueueModalKind = "talks_checkpoint" | "foreplay_checkpoint" | "penetration_checkpoint" | "penetration_end";

const HUSBAND_FIRST: ActorRole = "husband";
const FOREPLAY_DECKS: CardDeck[] = ["foreplay_husband", "foreplay_wife"];
export const PENETRATION_CHECKPOINT_COUNT = 3;
export const PENETRATION_SESSION_LIMIT = 5;

export function createId(prefix = "tlc"): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return `${prefix}_${crypto.randomUUID()}`;
  }
  return `${prefix}_${Date.now()}_${Math.random().toString(16).slice(2)}`;
}

export function modeStartDeck(mode: GameMode): CardDeck {
  return mode;
}

export function startSession(mode: GameMode, allCards: GameCard[], now = new Date()): GameSession {
  const session: GameSession = {
    id: createId("session"),
    mode,
    startedAt: now.toISOString(),
    currentDeck: modeStartDeck(mode),
    completedCardIds: [],
    skippedCardIds: [],
    talksCompleted: 0,
    foreplayCompleted: 0,
    foreplayHusbandCompleted: 0,
    foreplayWifeCompleted: 0,
    penetrationCompleted: 0,
    totalCompleted: 0,
    status: "active"
  };

  return prepareNextCard(session, allCards);
}

export function nextActor(lastActor?: ActorRole): ActorRole {
  if (!lastActor || lastActor === "both") return HUSBAND_FIRST;
  return lastActor === "husband" ? "wife" : "husband";
}

export function getCurrentCard(session: GameSession, allCards: GameCard[]): GameCard | undefined {
  return allCards.find((card) => card.id === session.currentCardId);
}

export function prepareNextCard(
  session: GameSession,
  allCards: GameCard[],
  random: RandomFn = Math.random
): GameSession {
  if (session.status !== "active") return session;
  if (session.currentCardId) return { ...session, status: "active" };

  const excluded = new Set([...session.completedCardIds, ...session.skippedCardIds]);

  if (session.currentDeck === "penetration" && session.penetrationCompleted >= PENETRATION_SESSION_LIMIT) {
    return {
      ...session,
      status: "completed",
      currentCardId: undefined,
      selectedPenetrationLevel: undefined,
      pendingNotice: "Sesi Penetrasi selesai."
    };
  }

  if (session.currentDeck === "penetration" && !session.selectedPenetrationLevel) {
    const allPenetrationDone = allCards
      .filter((card) => card.deck === "penetration")
      .every((card) => excluded.has(card.id));
    if (allPenetrationDone) {
      return {
        ...session,
        status: "completed",
        currentCardId: undefined,
        pendingNotice: "Semua kartu Penetrasi sudah selesai."
      };
    }
    return { ...session, status: "active", currentCardId: undefined };
  }

  const selection = selectNextCard(session, allCards, excluded, random);

  if (!selection.card) {
    if (session.currentDeck === "penetration" && session.selectedPenetrationLevel) {
      const allPenetrationDone = allCards
        .filter((card) => card.deck === "penetration")
        .every((card) => excluded.has(card.id));
      return {
        ...session,
        status: allPenetrationDone ? "completed" : "active",
        selectedPenetrationLevel: undefined,
        currentCardId: undefined,
        pendingNotice: allPenetrationDone
          ? "Semua kartu Penetrasi sudah selesai."
          : "Kartu untuk level ini sudah habis. Pilih level lain."
      };
    }
    return {
      ...session,
      status: "completed",
      currentCardId: undefined,
      pendingNotice: "Kartu untuk mode ini sudah habis. Kamu bisa mulai sesi baru atau pilih mode lain."
    };
  }

  return {
    ...session,
    status: "active",
    currentCardId: selection.card.id,
    pendingNotice: selection.fallbackUsed
      ? "Kartu untuk giliran ini sudah habis. Kita lanjut dengan kartu berikutnya."
      : undefined
  };
}

export function choosePenetrationLevel(
  session: GameSession,
  level: PenetrationLevel,
  allCards: GameCard[],
  random: RandomFn = Math.random
): GameSession {
  return prepareNextCard(
    {
      ...session,
      mode: "penetration",
      currentDeck: "penetration",
      currentCardId: undefined,
      selectedPenetrationLevel: level,
      pendingNotice: undefined,
      status: "active"
    },
    allCards,
    random
  );
}

export function switchToDeck(session: GameSession, deck: CardDeck, allCards: GameCard[]): GameSession {
  return prepareNextCard(
    {
      ...session,
      mode: deck,
      currentDeck: deck,
      currentCardId: undefined,
      selectedPenetrationLevel: undefined,
      pendingNotice: undefined,
      status: "active"
    },
    allCards
  );
}

export function selectNextCard(
  session: GameSession,
  allCards: GameCard[],
  excluded: Set<string>,
  random: RandomFn = Math.random
): { card?: GameCard; fallbackUsed: boolean } {
  if (session.currentDeck === "talks") {
    const preferredActor = nextActor(session.lastTalkActor);
    const card = selectTalksCard(allCards, preferredActor, excluded, random);
    if (card) return { card, fallbackUsed: false };
    const fallbackActor = preferredActor === "husband" ? "wife" : "husband";
    const fallback = selectTalksCard(allCards, fallbackActor, excluded, random);
    return { card: fallback, fallbackUsed: Boolean(fallback) };
  }

  if (session.currentDeck === "penetration") {
    return {
      card: selectPenetrationCard(allCards, session.selectedPenetrationLevel, excluded, random),
      fallbackUsed: false
    };
  }

  return { card: selectSequentialCard(allCards, session.currentDeck, excluded), fallbackUsed: false };
}

function selectTalksCard(
  allCards: GameCard[],
  actor: ActorRole,
  excluded: Set<string>,
  random: RandomFn
): GameCard | undefined {
  const pool = cardsForDeckActor(allCards, "talks", actor).filter((card) => !excluded.has(card.id));
  if (!pool.length) return undefined;
  return pool[Math.floor(random() * pool.length)];
}

function selectSequentialCard(allCards: GameCard[], deck: CardDeck, excluded: Set<string>): GameCard | undefined {
  const pool = cardsForDeck(allCards, deck).filter((card) => !excluded.has(card.id));
  return pool[0];
}

function selectPenetrationCard(
  allCards: GameCard[],
  level: PenetrationLevel | undefined,
  excluded: Set<string>,
  random: RandomFn
): GameCard | undefined {
  if (!level) return undefined;
  const pool = cardsForDeck(allCards, "penetration").filter((card) => card.level === level && !excluded.has(card.id));
  if (!pool.length) return undefined;
  return pool[Math.floor(random() * pool.length)];
}

export function cardsForDeckActor(allCards: GameCard[], deck: CardDeck, actor: ActorRole): GameCard[] {
  return cardsForDeck(allCards, deck)
    .filter((card) => card.actor === actor)
    .sort(sortBySequence);
}

export function cardsForDeck(allCards: GameCard[], deck: CardDeck): GameCard[] {
  return allCards.filter((card) => card.deck === deck).sort(sortBySequence);
}

export function completeCurrentCard(session: GameSession, card: GameCard): GameSession {
  const completedCardIds = uniqueAppend(session.completedCardIds, card.id);
  const counts = countsForCompletedCard(session, card);

  return {
    ...session,
    ...counts,
    totalCompleted: session.totalCompleted + 1,
    completedCardIds,
    currentCardId: undefined,
    selectedPenetrationLevel: card.deck === "penetration" ? undefined : session.selectedPenetrationLevel,
    lastTalkActor: card.deck === "talks" ? card.actor : session.lastTalkActor
  };
}

export function skipCurrentCard(session: GameSession, card: GameCard, allCards: GameCard[]): GameSession {
  const skipped = {
    ...session,
    skippedCardIds: uniqueAppend(session.skippedCardIds, card.id),
    currentCardId: undefined,
    selectedPenetrationLevel: card.deck === "penetration" ? undefined : session.selectedPenetrationLevel,
    lastTalkActor: card.deck === "talks" ? card.actor : session.lastTalkActor
  };
  return prepareNextCard(skipped, allCards);
}

export function reshufflePenetrationCard(
  session: GameSession,
  card: GameCard,
  allCards: GameCard[],
  random: RandomFn = Math.random
): GameSession {
  if (card.deck !== "penetration") return skipCurrentCard(session, card, allCards);
  if (session.penetrationCompleted >= PENETRATION_SESSION_LIMIT) {
    return {
      ...session,
      status: "completed",
      currentCardId: undefined,
      selectedPenetrationLevel: undefined,
      pendingNotice: "Sesi Penetrasi selesai."
    };
  }

  return prepareNextCard(
    {
      ...session,
      skippedCardIds: uniqueAppend(session.skippedCardIds, card.id),
      currentCardId: undefined,
      selectedPenetrationLevel: session.selectedPenetrationLevel ?? card.level,
      pendingNotice: undefined,
      status: "active"
    },
    allCards,
    random
  );
}

export function dueModalKinds(session: GameSession, completedCard: GameCard): QueueModalKind[] {
  const due: QueueModalKind[] = [];
  if (completedCard.deck === "talks" && session.talksCompleted > 0 && session.talksCompleted % 6 === 0) {
    due.push("talks_checkpoint");
  }
  if (completedCard.deck === "foreplay_husband" && session.foreplayHusbandCompleted > 0 && session.foreplayHusbandCompleted % 6 === 0) {
    due.push("foreplay_checkpoint");
  }
  if (completedCard.deck === "foreplay_wife" && session.foreplayWifeCompleted > 0 && session.foreplayWifeCompleted % 6 === 0) {
    due.push("foreplay_checkpoint");
  }
  if (completedCard.deck === "penetration" && session.penetrationCompleted === PENETRATION_CHECKPOINT_COUNT) {
    due.push("penetration_checkpoint");
  }
  if (completedCard.deck === "penetration" && session.penetrationCompleted >= PENETRATION_SESSION_LIMIT) {
    due.push("penetration_end");
  }
  return due;
}

export function resumeSession(session: GameSession, allCards: GameCard[]): GameSession {
  if (session.currentCardId) return { ...session, status: "active" };
  return prepareNextCard({ ...session, status: "active" }, allCards);
}

export function summarizeSession(session: GameSession, finalStatus: FinalStatus, now = new Date()): SessionSummary {
  return {
    id: session.id,
    startedAt: session.startedAt,
    endedAt: now.toISOString(),
    mode: session.mode,
    talksCompleted: session.talksCompleted,
    foreplayCompleted: session.foreplayCompleted,
    penetrationCompleted: session.penetrationCompleted,
    finalStatus
  };
}

export function isForeplayDeck(deck: CardDeck): boolean {
  return FOREPLAY_DECKS.includes(deck);
}

export function oppositeForeplayDeck(deck: CardDeck): CardDeck {
  return deck === "foreplay_husband" ? "foreplay_wife" : "foreplay_husband";
}

function countsForCompletedCard(session: GameSession, card: GameCard): Pick<
  GameSession,
  "talksCompleted" | "foreplayCompleted" | "foreplayHusbandCompleted" | "foreplayWifeCompleted" | "penetrationCompleted"
> {
  return {
    talksCompleted: session.talksCompleted + (card.deck === "talks" ? 1 : 0),
    foreplayCompleted: session.foreplayCompleted + (isForeplayDeck(card.deck) ? 1 : 0),
    foreplayHusbandCompleted: session.foreplayHusbandCompleted + (card.deck === "foreplay_husband" ? 1 : 0),
    foreplayWifeCompleted: session.foreplayWifeCompleted + (card.deck === "foreplay_wife" ? 1 : 0),
    penetrationCompleted: session.penetrationCompleted + (card.deck === "penetration" ? 1 : 0)
  };
}

function sortBySequence(a: GameCard, b: GameCard): number {
  return (a.sequence ?? 0) - (b.sequence ?? 0);
}

function uniqueAppend(values: string[], next: string): string[] {
  return values.includes(next) ? values : [...values, next];
}
